<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import Swal from 'sweetalert2'
import AppLogo from '@/components/AppLogo.vue'
import SessaoAnnotationsPanel from '@/components/SessaoAnnotationsPanel.vue'
import SessaoReplayer from '@/components/SessaoReplayer.vue'
import { getPublicSessao, loadPublicSessaoEventsIncremental } from '@/api/public'
import { getApiErrorMessage } from '@/api/client'
import type { PublicSessao } from '@/types/sessoes'
import { formatDateTime } from '@/utils/datetime'

definePage({
  meta: {},
})

const route = useRoute()

const shareToken = computed(() => {
  const { token } = route.params as { token?: string | string[] }
  return Array.isArray(token) ? (token[0] ?? '') : (token ?? '')
})

const sessao = ref<PublicSessao | null>(null)
const events = ref<unknown[]>([])
const loadingMeta = ref(true)
const loadingEvents = ref(false)
const loadProgress = ref<number | null>(null)
const errorMessage = ref('')
const detailsOpen = ref(false)

const metadadosEntries = computed(() => {
  if (!sessao.value) return []
  return Object.entries(sessao.value.metadados ?? {})
})

const exportMeta = computed(() => {
  if (!sessao.value) return null
  return { ...sessao.value } as Record<string, unknown>
})

const replayerRef = ref<{
  seekTo: (offsetMs: number, resume?: boolean) => void
} | null>(null)

function onAnnotationSeek(offsetMs: number): void {
  replayerRef.value?.seekTo(offsetMs)
}

async function load(): Promise<void> {
  loadingMeta.value = true
  loadingEvents.value = false
  loadProgress.value = null
  errorMessage.value = ''
  events.value = []

  try {
    const meta = await getPublicSessao(shareToken.value)
    sessao.value = meta
    loadingMeta.value = false
    loadingEvents.value = true
    loadProgress.value = 0
    await loadPublicSessaoEventsIncremental(shareToken.value, meta.chunks_count, {
      onChunk: (chunk) => {
        events.value.push(...chunk)
      },
      onProgress: (loaded, total) => {
        loadProgress.value = total > 0 ? (loaded / total) * 100 : 100
      },
    })
  } catch (error) {
    sessao.value = null
    events.value = []
    errorMessage.value = getApiErrorMessage(error, 'Sessão não encontrada ou link expirado')
    await Swal.fire({
      icon: 'error',
      title: 'Replay indisponível',
      text: errorMessage.value,
    })
  } finally {
    loadingMeta.value = false
    loadingEvents.value = false
    loadProgress.value = null
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="min-vh-100 bg-light d-flex flex-column">
    <header class="border-bottom bg-white shadow-sm">
      <div class="container py-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
        <AppLogo />
        <span class="text-muted small">Replay público</span>
      </div>
    </header>

    <main class="container py-4 flex-grow-1">
      <div v-if="loadingMeta" class="text-muted">Carregando sessão…</div>

      <div v-else-if="errorMessage" class="alert alert-light border">
        {{ errorMessage }}
      </div>

      <template v-else-if="sessao">
        <div class="mb-3">
          <h1 class="h5 mb-1">{{ sessao.usuario_externo_nome || 'Sessão gravada' }}</h1>
          <p class="text-muted small mb-0">
            {{ formatDateTime(sessao.started_at) ?? '—' }}
            <span v-if="sessao.ended_at"> → {{ formatDateTime(sessao.ended_at) }}</span>
            · {{ sessao.status }}
          </p>
          <div v-if="metadadosEntries.length > 0" class="d-flex flex-wrap gap-1 mt-2">
            <span
              v-for="[chave, valor] in metadadosEntries.slice(0, 4)"
              :key="chave"
              class="badge text-bg-light border"
            >
              {{ chave }}: {{ valor }}
            </span>
            <button
              type="button"
              class="btn btn-link btn-sm p-0 align-baseline"
              @click="detailsOpen = !detailsOpen"
            >
              {{ detailsOpen ? 'Ocultar detalhes' : 'Detalhes' }}
            </button>
          </div>
        </div>

        <div
          v-if="detailsOpen && metadadosEntries.length > 0"
          class="card shadow-sm border-0 mb-3"
        >
          <div class="card-body small">
            <strong>Metadados</strong>
            <ul class="list-unstyled mb-0 mt-1">
              <li v-for="[chave, valor] in metadadosEntries" :key="chave">
                <code>{{ chave }}</code>: {{ valor }}
              </li>
            </ul>
          </div>
        </div>

        <SessaoReplayer
          ref="replayerRef"
          :events="events"
          :loading="loadingEvents"
          :load-progress="loadProgress"
          :export-meta="exportMeta"
          :annotations="sessao.annotations ?? []"
        />

        <div class="mt-3">
          <SessaoAnnotationsPanel
            readonly
            :annotations="sessao.annotations ?? []"
            @seek="onAnnotationSeek"
          />
        </div>
      </template>
    </main>
  </div>
</template>
